// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { cx } from './cx';
import styles from './ToggleChip.module.css';

export interface ToggleChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  pressed: boolean;
  onChange: (pressed: boolean) => void;
  /** Leading icon/glyph, sized to --space-4; aria-hidden. */
  icon?: ReactNode;
  /** `md` (default, 32px) — toolbars. `sm` (28px) — dense rows. */
  size?: 'sm' | 'md';
  ref?: Ref<HTMLButtonElement>;
}

/**
 * A pill that toggles one filter on and off (Tabel's Vigtige, Fejldetektion):
 * an `aria-pressed` button, accent-filled while on. `className` lands on the
 * `<button>`; no phone growth — it lives in bars, as IconButton does.
 */
export function ToggleChip({ pressed, onChange, icon, size = 'md', className, onClick, children, ...rest }: ToggleChipProps) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cx(styles.chip, styles[size], className)}
      {...rest}
      onClick={(e) => {
        // A caller's onClick runs first; preventDefault() keeps the state.
        onClick?.(e);
        if (!e.defaultPrevented) onChange(!pressed);
      }}
    >
      {icon != null && icon !== false && (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}
